
import { computed, defineComponent, onBeforeMount, PropType, reactive, watch } from 'vue'

// components
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'
import RadioGroup from '@/common/components/UI/RadioGroup/index.vue'
import FileReader from '@/common/components/UI/FileReader/index.vue'

// constants
import { PRICE_MASK } from '@/core/constants/masks.constants'
import {
    PAYMENT_METHOD_OPTIONS,
    LOAD_CAPACITY_OPTIONS,
    LOADING_TYPE_OPTIONS,
    PAYMENT_METHODS_OBJECT,
    TRANSPORT_TYPES,
} from '@/core/constants/create_order.constants'

// realisations
import { FTL } from '@/core/realisations/FTL.realisation'

// validation
import useVuelidate from '@vuelidate/core'

// types
import { FTLLoadCapacityT, FTLLoadTransportTypesT } from '@/core/types/ftl.types'
import { CompletedCarT } from '@/core/types/create_order.types'
import { CargoFTLBidT } from '@/core/types/create_order.types'

// composable
import useHelpers from '@/common/composable/useHelpers'
import { requiredIf, minLength, decimal } from '@vuelidate/validators'
import { CarInfoT } from '@/core/types/ftl_bids.types'
import { DataState } from '@/core/classes/DataState.class'

export default defineComponent({
    components: {
        'the-input': TheInput,
        'the-select': TheSelect,
        'the-button': TheButton,
        checkbox: Checkbox,
        'radio-group': RadioGroup,
        FileReader,
    },
    props: {
        stepNumber: {
            type: Number,
            default: 0,
        },
        ftlType: {
            type: Number,
            default: null,
        },
        opened: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        carInfo: {
            type: Object as PropType<CarInfoT>,
            default: null,
        },
        declaredPrice: {
            type: Number,
            default: null,
        },
        cargoName: {
            type: String,
            default: null,
        },
        paymentMethod: {
            type: Number,
            default: null,
        },
        transportType: {
            type: Number,
            default: null,
        },
        completedCargo: {
            type: Object as PropType<CargoFTLBidT>,
            default: null,
        },
        isOnCargoStep: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['submit', 'reset'],
    setup(props, { emit }) {
        const state = reactive({
            // inputs
            load_capacity: null as string | null,
            description: null as string | null,
            cubature: null as string | null,
            loading_type: null as string | null,
            temperature_mode: null as string | null,
            declared_price: null as number | null,
            transport_type: null as number | null,
            // checkboxes
            oversized_cargo: 0,
            is_stacked: 0,
            is_need_car: 0,
            //
            is_dangerous: 0,
            invoice_files: [] as File[],
            certificate_of_safety_files: [] as File[],
            //
            is_ensure: 0,
            cargo_name: null as string | null,
            //
            annotation: null as string | null,
            payment_method: 3,
        })

        const files_state = reactive({
            is_loading: false,
        })

        const data_state = reactive({ transport_types: new DataState<FTLLoadTransportTypesT>({}) })

        const isAuto = computed(() => {
            return props.completedCargo && props.completedCargo.shipment_type === 1
        })

        const CarValidation = {
            load_capacity: {
                required: requiredIf(
                    () => props.ftlType !== 1 && Boolean(!state.is_need_car) && isAuto.value
                ),
            },
            cubature: {
                required: requiredIf(
                    () => props.ftlType !== 1 && Boolean(!state.is_need_car) && isAuto.value
                ),
            },
            loading_type: {
                required: requiredIf(
                    () => props.ftlType !== 1 && Boolean(!state.is_need_car) && isAuto.value
                ),
            },
            description: {
                required: requiredIf(() => Boolean(!state.is_need_car)),
            },
            invoice_files: {
                required: requiredIf(() => Boolean(state.is_dangerous)),
            },
            certificate_of_safety_files: {
                required: requiredIf(() => Boolean(state.is_dangerous)),
            },
            cargo_name: {
                required: requiredIf(() => Boolean(state.is_ensure)),
                minLength: minLength(3),
            },
            declared_price: {
                required: requiredIf(() => Boolean(state.is_ensure)),
                decimal
            },
        }

        const state_form$ = useVuelidate(CarValidation, state)

        const completed_state = reactive({
            car: null as CompletedCarT | null,
        })

        const resetInputs = () => {
            state.load_capacity = null
            state.description = null
            state.loading_type = null
            state.cubature = null
            state.transport_type = null
            state.temperature_mode = null
            state.declared_price = null
            state.annotation = null

            state.payment_method = 3
            state.oversized_cargo = 0
            state.is_dangerous = 0
            state.is_ensure = 0
            state.cargo_name = null
            state.is_stacked = 0
        }

        const resetForm = () => {
            state.is_need_car = 0

            state_form$.value.$reset()

            completed_state.car = null
            emit('reset')
        }

        const resetAutoValues = () => {
            state.load_capacity = null
            state.cubature = null
            state.loading_type = null
            state.transport_type = null
        }

        const emitSubmit = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid || props.disabled) {
                return
            }

            if (
                ((!state.load_capacity && props.ftlType !== 1) ||
                    (!state.loading_type && props.ftlType !== 1) ||
                    !state.description) &&
                isAuto.value &&
                !state.is_need_car
            ) {
                return
            }

            if (!isAuto.value) resetAutoValues()

            completed_state.car = {
                ...state,
                load_capacity: state.load_capacity,
                loading_type: state.loading_type,
            }

            emit('submit', { ...state, is_need_car: state.is_need_car ? 0 : 1 })
        }

        const updateCar = () => {
            completed_state.car = null
        }

        const capacity_state = reactive({
            is_loading: false,
            list: [] as FTLLoadCapacityT[],
        })

        const loadCapacity = async () => {
            try {
                capacity_state.is_loading = true
                const response = await FTL.getCapacity()
                capacity_state.list = response.data
            } catch (error) {
                console.error(error)
            } finally {
                capacity_state.is_loading = false
            }
        }

        onBeforeMount(async () => {
            await loadCapacity()
            await loadTransportTypes()
            data_state.transport_types.is_failed = true

            if (props.carInfo) {
                state.cubature = props.carInfo.cubature
                state.temperature_mode = props.carInfo.temperature_mode
                state.loading_type = props.carInfo.loading_type
                state.load_capacity = props.carInfo.load_capacity
                state.oversized_cargo = props.carInfo.oversized_cargo
                state.is_need_car = props.carInfo.is_need_car ? 0 : 1
            }

            if (state.load_capacity) onCapacityChange(state.load_capacity)

            if (props.declaredPrice) {
                state.is_ensure = 1
                state.declared_price = props.declaredPrice
                state.cargo_name = props.cargoName
            }
            if (props.paymentMethod) state.payment_method = props.paymentMethod
            if (props.transportType) state.transport_type = props.transportType
        })

        const onCapacityChange = (capacity: string) => {
            const { findObjectByValue } = useHelpers()
            const capacity_obj = findObjectByValue('load_capacity', capacity, capacity_state.list)

            if (!capacity_obj) return
            state.cubature = capacity_obj.cubature
        }

        const loadTransportTypes = async () => {
            try {
                data_state.transport_types.is_loading = true
                const response = await FTL.getTransportTypes()
                data_state.transport_types.list = response.data
            } catch (error) {
                console.error(error)
            } finally {
                data_state.transport_types.is_loading = false
            }
        }

        const onIsCarChange = () => {
            if (state.is_need_car) resetInputs()
        }

        const resetFiles = () => {
            state.invoice_files = []
            state.certificate_of_safety_files = []
        }

        const onIsDangerousChange = (is_dangerous: number) => {
            if (is_dangerous === 0) resetFiles()
        }

        const onChangeEnsure = (is_ensure: number) => {
            if (is_ensure === 0) {
                state.cargo_name = null
                state.declared_price = null
            }
        }

        watch(
            () => props.opened,
            (value) => {
                if (!value && props.isOnCargoStep) {
                    resetForm()
                }
            }
        )

        const isCompleted = computed(() => Boolean(completed_state.car))
        const getTransportName = computed(
            () => TRANSPORT_TYPES.find((transport) => transport.id === state.transport_type)?.name
        )

        return {
            state,
            files_state,
            completed_state,
            capacity_state,
            data_state,
            state_form$,
            //
            emitSubmit,
            resetForm,
            updateCar,
            onCapacityChange,
            onIsCarChange,
            onIsDangerousChange,
            onChangeEnsure,
            //
            PRICE_MASK,
            LOADING_TYPE_OPTIONS,
            PAYMENT_METHOD_OPTIONS,
            LOAD_CAPACITY_OPTIONS,
            PAYMENT_METHODS_OBJECT,
            TRANSPORT_TYPES,
            //
            isAuto,
            isCompleted,
            getTransportName,
        }
    },
})
